import { useEffect, useState } from 'react';
import { Image, View } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { Button } from '@/components/ui/therapist/Button';
import Dropdown from '@/components/ui/therapist/Dropdown';
import { Input as InputPatient } from 'react-native-elements';
import { Input } from '@/components/ui/therapist/Input';
import { PrimaryButton } from '@/components/Button';
import { Text } from '@/components/ui/therapist/Text';
import { logError } from '@/utils/logHandling';
import {
  fetchMarketingChannels,
  MarketingChannelType,
} from '@/api/services/marketingChannels';
import { saveMarketingChannelSelection } from '@/api/services/user';
import { useNavigation } from '@react-navigation/native';

import { isPatientApp } from '@/config/constants';
import { IS_WEB } from '@/utils/platform';
import { styles } from './styles';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Dropdown as DropdownPatient } from 'react-native-element-dropdown';
import colors from '@/config/colors';

export const MarketingChannelQuestionnaire = () => {
  const [selectedChannel, setSelectedChannel] =
    useState<MarketingChannelType | null>(null);
  const [otherResponse, setOtherResponse] = useState('');
  const [marketingChannels, setMarketingChannels] = useState<
    MarketingChannelType[]
  >([]);
  const { navigate, setOptions } =
    useNavigation<
      StackNavigationProp<PatientStackParamList | TherapistStackParamList>
    >();

  useEffect(() => {
    setOptions({
      headerRight: () => <></>,
      headerLeft: () =>
        isPatientApp ? (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              source={require('../../assets/small-neolexon-white-logo.png')}
              style={{ width: 26, height: 28, marginRight: 4 }}
              resizeMode="contain"
            />
            <View>
              <Image
                source={require('../../assets/neolexon-name.png')}
                style={{ width: 120, height: 35 }}
                resizeMode="contain"
              />
            </View>
          </View>
        ) : (
          <></>
        ),
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const marketingChannels = await fetchMarketingChannels();
        setMarketingChannels(marketingChannels);
      } catch (error) {
        logError('Failed to fetch marketing channels', error);
      }
    })();
  }, []);

  const handleOnPress = async (buttonPressed?: 'Skip') => {
    try {
      const payload = {
        marketingChannelResponse: otherResponse,
        marketingChannelSelection:
          buttonPressed === 'Skip'
            ? 'skipped'
            : selectedChannel?.value || 'skipped',
      };
      await saveMarketingChannelSelection(payload);
      navigate('Home');
    } catch (error) {
      logError('Failed to save marketing channel selection', error);
      navigate('Home');
    }
  };

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <KeyboardAwareScrollView
        enableOnAndroid={true}
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View style={styles.marketingChannelWrapper}>
          <View style={{ alignItems: 'center' }}>
            <View style={styles.marketingHeaderWrapper}>
              <Text style={{ fontWeight: 'bold', fontSize: 28 }}>
                🙏 Wie haben Sie von uns erfahren?
              </Text>
              <Text style={{ fontSize: 22 }}>
                Mit Ihrer Antwort helfen Sie uns, unsere Kommunikation zu
                verbessern.
              </Text>
            </View>
            <View style={{ width: IS_WEB ? '125%' : '80%' }}>
              <View style={styles.marketingFormWrapper}>
                {isPatientApp ? (
                  <>
                    <Text style={styles.patientLabel}>
                      Bitte wählen Sie einen Punkt aus der Liste
                    </Text>
                    <DropdownPatient
                      style={styles.patientDropdownStyle}
                      containerStyle={styles.patientDropdownContainer}
                      itemTextStyle={styles.patientDropdownTextStyle}
                      selectedTextStyle={styles.patientDropdownTextStyle}
                      placeholderStyle={{
                        fontSize: 18,
                        color: colors.darkGrey,
                      }}
                      labelField="label"
                      valueField="value"
                      placeholder="Bitte auswählen"
                      value={selectedChannel?.value}
                      data={marketingChannels.map((channel) => ({
                        label: channel.displayName,
                        value: channel.value,
                      }))}
                      onChange={(selectedChannel) => {
                        const channel = marketingChannels.find(
                          (channel) => channel.value === selectedChannel.value,
                        ) as MarketingChannelType;
                        setSelectedChannel(channel);
                      }}
                    />
                  </>
                ) : (
                  <Dropdown
                    label="Bitte wählen Sie einen Punkt aus der Liste"
                    data={marketingChannels.map((channel) => ({
                      label: channel.displayName,
                      value: channel.value,
                    }))}
                    onSelectChange={(selectedChannel) => {
                      const channel = marketingChannels.find(
                        (channel) => channel.value === selectedChannel[0],
                      ) as MarketingChannelType;
                      setSelectedChannel(channel);
                    }}
                  />
                )}
                {selectedChannel?.value === 'other' ? (
                  isPatientApp ? (
                    <>
                      <Text style={styles.patientLabel}>
                        Bitte geben Sie an, wie Sie von uns erfahren haben
                      </Text>
                      <InputPatient
                        style={{ outlineStyle: 'none' }}
                        value={otherResponse}
                        onChangeText={(text) => setOtherResponse(text)}
                      />
                    </>
                  ) : (
                    <Input
                      label="Bitte geben Sie an, wie Sie von uns erfahren haben"
                      value={otherResponse}
                      onChange={(text) => setOtherResponse(text)}
                    />
                  )
                ) : null}
              </View>
              <View style={styles.marketingButtonWrapper}>
                {isPatientApp ? (
                  <>
                    <PrimaryButton
                      label="Überspringen"
                      look="neutral"
                      onPress={() => handleOnPress('Skip')}
                    />
                    <PrimaryButton
                      onPress={() => handleOnPress()}
                      label="Antwort abschicken"
                      disabled={!selectedChannel}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      label="Überspringen"
                      look="secondary"
                      onPress={() => handleOnPress('Skip')}
                    />
                    <Button
                      label="Antwort abschicken"
                      onPress={() => handleOnPress()}
                      disabled={!selectedChannel}
                    />
                  </>
                )}
              </View>
            </View>
          </View>
        </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  );
};
